import { useEffect, useMemo, useState } from 'react';
import type { AssignableCourseI, DevelopmentPlanBucketsI, DevelopmentPlanEntryI, DevelopmentPlanOrigin } from 'types';

interface Props {
  buckets: DevelopmentPlanBucketsI | null;
  /**
   * Opcionales a los que el empleado tiene derecho y todavía no ha iniciado.
   * Se muestran dentro de la pestaña "Opcionales" con estatus "Sin iniciar".
   */
  availableOptional?: AssignableCourseI[];
  initialPageSize?: number;
}

/**
 * Una fila de la tabla del plan.
 *
 * Lleva los dos orígenes posibles y exactamente uno relleno: `entry` cuando hay
 * inscripción, `available` cuando el curso está disponible pero sin iniciar.
 * Se modela así, y no con un `DevelopmentPlanEntryI` de campos vacíos, porque un
 * curso sin iniciar no tiene identificador de inscripción ni estatus: fingir que
 * los tiene obliga a comprobar cadenas vacías en cada consumidor.
 */
export interface PlanTableRow {
  id: string;
  name: string;
  duration: number | null;
  entry: DevelopmentPlanEntryI | null;
  available: AssignableCourseI | null;
}

/**
 * Orden de las pestañas, de lo más general a lo más específico.
 *
 * No es el orden de precedencia del backend, que va al revés: aquí manda cómo
 * lo lee el administrador, que empieza por lo que aplica a toda la empresa.
 */
const ORIGINS: DevelopmentPlanOrigin[] = ['enterprise', 'department', 'jobTitle', 'manual', 'optional'];

const EMPTY: DevelopmentPlanBucketsI = {
  enterprise: [],
  department: [],
  jobTitle: [],
  manual: [],
  optional: [],
};

const toEnrolledRow = (entry: DevelopmentPlanEntryI): PlanTableRow => ({
  id: entry.userCourseId,
  name: entry.name,
  duration: entry.duration,
  entry,
  available: null,
});

// El prefijo evita que un curso disponible choque con la inscripción de otro:
// los dos identificadores son UUID y vivirían en la misma tabla.
const toAvailableRow = (course: AssignableCourseI): PlanTableRow => ({
  id: `available-${course.id}`,
  name: course.name,
  duration: course.duration,
  entry: null,
  available: course,
});

/**
 * Estado de la tabla del plan: pestaña, búsqueda y paginación.
 *
 * Todo se resuelve en memoria. El plan de un empleado son decenas de cursos, no
 * miles: pedir una página al servidor por cada clic costaría más de lo que
 * ahorra, y el backend ya entrega los cinco buckets en una sola llamada.
 */
const useDevelopmentPlanTable = ({ buckets, availableOptional = [], initialPageSize = 10 }: Props) => {
  const [origin, setOriginState] = useState<DevelopmentPlanOrigin>('enterprise');
  const [search, setSearchState] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const safeBuckets = buckets ?? EMPTY;

  /**
   * Los contadores cuentan filas, no inscripciones.
   *
   * Un opcional disponible se ve en la tabla, así que suma en el chip de su
   * pestaña. Contar solo inscripciones dejaba "Opcionales" en 0 con filas a la
   * vista, y eso se lee como un error de datos.
   */
  const tabs = useMemo(
    () =>
      ORIGINS.map(value => ({
        value,
        count: (safeBuckets[value]?.length ?? 0) + (value === 'optional' ? availableOptional.length : 0),
      })),
    [safeBuckets, availableOptional]
  );

  /**
   * Cuántos cursos ve el empleado, sumando los cinco orígenes más los
   * opcionales disponibles.
   *
   * Sumar es correcto porque los buckets son exclusivos: el backend coloca cada
   * curso en exactamente uno, por orden de precedencia, y deduplica por
   * `userCourseId`. Los disponibles no tienen inscripción, así que tampoco
   * pueden estar ya en un bucket.
   *
   * No depende ni de la pestaña ni de la búsqueda: es todo lo que hay, no lo
   * que se está viendo. Para eso último está `totalItems`.
   */
  const totalCourses = useMemo(
    () => ORIGINS.reduce((total, value) => total + (safeBuckets[value]?.length ?? 0), 0) + availableOptional.length,
    [safeBuckets, availableOptional]
  );

  /**
   * Filas del origen activo, con los disponibles añadidos solo en "Opcionales".
   *
   * Se juntan aquí, antes de filtrar y paginar, para que la búsqueda y el
   * paginador los traten igual que a los inscritos. Pegarlos después de paginar
   * haría que el paginador mintiera sobre cuántas filas hay.
   */
  const allRows: PlanTableRow[] = useMemo(() => {
    const enrolled = (safeBuckets[origin] ?? []).map(toEnrolledRow);
    if (origin !== 'optional') return enrolled;

    return [...enrolled, ...availableOptional.map(toAvailableRow)];
  }, [safeBuckets, origin, availableOptional]);

  const filtered: PlanTableRow[] = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase();
    if (!needle) return allRows;

    return allRows.filter(row => row.name.toLocaleLowerCase().includes(needle));
  }, [allRows, search]);

  const totalItems = filtered.length;
  const totalPages = Math.ceil(totalItems / pageSize);

  // Si la página actual se quedó fuera de rango —al filtrar, o al cambiar el
  // tamaño— la tabla mostraría un vacío que parece un error de datos.
  useEffect(() => {
    if (page > totalPages && totalPages > 0) setPage(totalPages);
  }, [page, totalPages]);

  const rows = useMemo(() => filtered.slice((page - 1) * pageSize, page * pageSize), [filtered, page, pageSize]);

  const setOrigin = (value: DevelopmentPlanOrigin) => {
    setOriginState(value);
    setPage(1);
  };

  const setSearch = (value: string) => {
    setSearchState(value);
    setPage(1);
  };

  return {
    origin,
    setOrigin,
    tabs,
    totalCourses,
    search,
    setSearch,
    page,
    setPage,
    pageSize,
    setPageSize,
    rows,
    totalItems,
    totalPages,
  };
};

export default useDevelopmentPlanTable;
